<template>
  <div class="ai-scheduler-dashboard">
    <!-- Header -->
    <header class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <h1>🤖 AI Schedule Generator</h1>
          <p>Multi-tenant AI-powered workforce scheduling</p>
        </div>
        
        <div class="header-right">
          <div class="service-status" :class="{ 'connected': isServiceAuthenticated, 'error': serviceError }">
            <span class="status-indicator"></span>
            <span class="status-text">
              {{ isServiceAuthenticated ? 'Service Connected' : 'Service Disconnected' }}
            </span>
          </div>
          
          <div class="claude-status" :class="{ 'connected': claudeStatus?.available, 'loading': claudeStatusLoading }">
            <span class="status-indicator"></span>
            <span class="status-text">
              {{ claudeStatusLoading ? 'Checking Claude AI...' : (claudeStatus?.available ? 'Claude AI Ready' : 'Claude AI Unavailable') }}
            </span>
          </div>
          
          <button 
            v-if="selectedTenant" 
            @click="trainMLModel" 
            class="nav-btn ml-training-btn"
            :disabled="mlTraining || !isServiceAuthenticated"
            title="Train ML model on reviewed schedules"
          >
            {{ mlTraining ? '⏳ Training...' : '🎓 Train ML Model' }}
          </button>
          
          <button v-if="selectedTenant" @click="goBack" class="nav-btn">
            🏢 Change Company
          </button>
        </div>
      </div>
    </header>

    <!-- Service Authentication Error -->
    <div v-if="serviceError" class="service-error">
      <div class="error-content">
        <div class="error-icon">🔐</div>
        <h3>Service Authentication Failed</h3>
        <p>{{ serviceError }}</p>
        <p class="error-hint">
          Make sure VITE_AI_CLIENT_ID and VITE_AI_CLIENT_SECRET are configured in your environment.
        </p>
        <button @click="retryService" class="retry-service-btn">
          🔄 Retry Connection
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <main v-else class="dashboard-main">
      
      <!-- Tenant Selection View -->
      <TenantSelector
        v-if="currentView === 'tenant-selection'"
        @tenant-selected="handleTenantSelected"
        @generate-schedule="handleGenerateSchedule"
        @view-proposals="handleViewProposals"
      />

      <!-- Proposal Generation View -->
      <ProposalGenerator
        v-if="currentView === 'generate-proposal'"
        :tenant="selectedTenant"
        @back="goToTenantSelection"
        @proposal-generated="handleProposalGenerated"
        @review-proposal="handleReviewProposal"
        @proposal-approved="handleProposalApproved"
      />

      <!-- Proposals List View -->
      <ProposalsList
        v-if="currentView === 'proposals-list'"
        :tenant="selectedTenant"
        @back="goBack"
        @review-proposal="handleReviewProposal"
        @generate-new="handleGenerateSchedule"
      />

      <!-- Proposal Review View -->
      <ProposalReviewer
        v-if="currentView === 'review-proposal'"
        :proposal="selectedProposal"
        :tenant="selectedTenant"
        @back="goToPreviousView"
        @status-updated="handleProposalStatusUpdated"
      />

    </main>

    <!-- Loading overlay -->
    <div v-if="initializing" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <h3>Initializing AI Scheduler...</h3>
        <p>Connecting to backend services</p>
      </div>
    </div>

    <!-- Navigation breadcrumbs -->
    <nav v-if="!initializing" class="breadcrumbs">
      <button 
        v-for="(crumb, index) in breadcrumbs" 
        :key="crumb.view"
        @click="navigateTo(crumb.view)"
        class="breadcrumb"
        :class="{ active: index === breadcrumbs.length - 1 }"
      >
        {{ crumb.label }}
      </button>
    </nav>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAISchedulerStore } from '../../stores/aiScheduler.js'
import TenantSelector from './TenantSelector.vue'
import ProposalGenerator from './ProposalGenerator.vue'
import ProposalsList from './ProposalsList.vue'
import ProposalReviewer from './ProposalReviewer.vue'

const aiSchedulerStore = useAISchedulerStore()

// State
const initializing = ref(true)
const currentView = ref('tenant-selection')
const selectedTenant = ref(null)
const selectedProposal = ref(null)
const previousView = ref(null)

// Service state
const serviceError = ref(null)
const claudeStatus = ref(null)
const claudeStatusLoading = ref(false)
const mlTraining = ref(false)

// Computed properties
const isServiceAuthenticated = computed(() => aiSchedulerStore.isServiceAuthenticated)

const breadcrumbs = computed(() => {
  const crumbs = [
    { view: 'tenant-selection', label: '🏢 Select Company' }
  ]

  if (selectedTenant.value) {
    if (currentView.value === 'generate-proposal') {
      crumbs.push({ view: 'generate-proposal', label: '🤖 Generate Schedule' })
    } else if (currentView.value === 'proposals-list') {
      crumbs.push({ view: 'proposals-list', label: '📋 View Proposals' })
    } else if (currentView.value === 'review-proposal') {
      crumbs.push({ view: 'proposals-list', label: '📋 Proposals' })
      crumbs.push({ view: 'review-proposal', label: '👁️ Review Proposal' })
    }
  }

  return crumbs
})

// Navigation methods
const navigateTo = (view) => {
  previousView.value = currentView.value
  currentView.value = view
  
  // Clear selections when navigating back
  if (view === 'tenant-selection') {
    selectedTenant.value = null
    selectedProposal.value = null
  } else if (view === 'proposals-list') {
    selectedProposal.value = null
  }
}

const goToTenantSelection = () => {
  navigateTo('tenant-selection')
}

const goBack = () => {
  if (currentView.value === 'review-proposal') {
    navigateTo('proposals-list')
  } else if (currentView.value === 'generate-proposal' || currentView.value === 'proposals-list') {
    navigateTo('tenant-selection')
  }
}

const goToPreviousView = () => {
  if (previousView.value) {
    currentView.value = previousView.value
  } else {
    goBack()
  }
}

// Event handlers
const handleTenantSelected = (tenant) => {
  selectedTenant.value = tenant
}

const handleGenerateSchedule = (tenant) => {
  if (tenant) {
    selectedTenant.value = tenant
  }
  navigateTo('generate-proposal')
}

const handleViewProposals = (tenant) => {
  if (tenant) {
    selectedTenant.value = tenant
  }
  navigateTo('proposals-list')
}

const handleProposalGenerated = (proposal) => {
  selectedProposal.value = proposal
  
  // Show success notification
  showNotification('Schedule proposal generated successfully!', 'success')
}

const handleReviewProposal = (proposal) => {
  selectedProposal.value = proposal
  navigateTo('review-proposal')
}

const handleProposalApproved = (proposal) => {
  showNotification('Schedule proposal approved and deployed!', 'success')
  
  // Navigate back to proposals list
  navigateTo('proposals-list')
}

const handleProposalStatusUpdated = (status, notes, regeneratedProposal = null) => {
  const statusMessages = {
    approved: 'Proposal approved and deployed to tenant!',
    rejected: 'Proposal rejected with feedback.',
    reviewing: 'Proposal marked for review and changes.',
    regenerated: '✨ New proposal generated based on your feedback!'
  }
  
  // If a new proposal was regenerated, update the selected proposal
  if (status === 'regenerated' && regeneratedProposal) {
    selectedProposal.value = regeneratedProposal
    showNotification(statusMessages[status], 'success')
    // Stay on review page to show the new proposal
    return
  }
  
  showNotification(statusMessages[status] || 'Proposal status updated.', 'success')
  
  // Navigate back to proposals list
  navigateTo('proposals-list')
}

// Service management
const initializeService = async () => {
  console.log('🚀 AISchedulerDashboard: initializeService() called')
  initializing.value = true
  serviceError.value = null
  
  try {
    console.log('  Calling aiSchedulerStore.initializeService()')
    await aiSchedulerStore.initializeService()
    console.log('  ✅ Service initialized successfully')
    
    // Check Claude AI status after service initialization
    await checkClaudeAIStatus()
  } catch (error) {
    console.error('❌ Failed to initialize AI Scheduler service:', error)
    serviceError.value = error.message
  } finally {
    initializing.value = false
  }
}

const checkClaudeAIStatus = async () => {
  claudeStatusLoading.value = true
  
  try {
    claudeStatus.value = await aiSchedulerStore.checkClaudeStatus()
  } catch (error) {
    console.warn('Claude AI status check failed:', error)
    claudeStatus.value = { available: false, error: error.message }
  } finally {
    claudeStatusLoading.value = false
  }
}

const retryService = async () => {
  await initializeService()
}

const trainMLModel = async () => {
  if (mlTraining.value) return
  
  const confirmed = confirm(
    'Train ML Model?\n\n' +
    'This will train the ML model on all reviewed schedules (approved and rejected).\n' +
    'Your review feedback will help the model learn your scheduling preferences.\n\n' +
    'Note: You need at least 20 reviewed schedules for training.'
  )
  
  if (!confirmed) return
  
  mlTraining.value = true
  
  try {
    const result = await aiSchedulerStore.trainMLModel(50)
    
    if (result.status === 'success') {
      showNotification(
        `✅ ML Model trained successfully! ${result.message}`,
        'success'
      )
    } else if (result.status === 'insufficient_data') {
      showNotification(
        `⚠️ ${result.message} - ${result.note}`,
        'error'
      )
    }
  } catch (error) {
    console.error('ML training failed:', error)
    showNotification(
      `❌ Training failed: ${error.message}`,
      'error'
    )
  } finally {
    mlTraining.value = false
  }
}

// Notification system (simple implementation)
const showNotification = (message, type = 'info') => {
  // Create a simple toast notification
  const notification = document.createElement('div')
  notification.className = `notification notification-${type}`
  notification.textContent = message
  notification.style.cssText = `
    position: fixed;
    top: 2rem;
    right: 2rem;
    z-index: 1000;
    padding: 1rem 1.5rem;
    background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
    color: white;
    border-radius: 0.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    animation: slideIn 0.3s ease-out;
  `
  
  document.body.appendChild(notification)
  
  // Remove after 5 seconds
  setTimeout(() => {
    notification.style.animation = 'slideOut 0.3s ease-in forwards'
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 300)
  }, 5000)
}

// Lifecycle
onMounted(async () => {
  await initializeService()
})

// Watch for service authentication changes
watch(() => isServiceAuthenticated.value, (isAuth) => {
  // Service authentication state changed
})

// Add CSS for notifications
const addNotificationStyles = () => {
  if (!document.getElementById('ai-scheduler-styles')) {
    const style = document.createElement('style')
    style.id = 'ai-scheduler-styles'
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `
    document.head.appendChild(style)
  }
}

onMounted(() => {
  addNotificationStyles()
})
</script>

<style scoped>
.ai-scheduler-dashboard {
  min-height: 100vh;
  background: #f8fafc;
}

.dashboard-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1.5rem 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left h1 {
  margin: 0;
  color: #1e293b;
  font-size: 1.875rem;
  font-weight: 700;
  line-height: 1.2;
}

.header-left p {
  margin: 0.25rem 0 0 0;
  color: #64748b;
  font-size: 1rem;
}

.header-right {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.service-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.service-status.connected {
  background: #dcfce7;
  color: #166534;
}

.service-status.error {
  background: #fef2f2;
  color: #991b1b;
}

.claude-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.claude-status.connected {
  background: #e0f2fe;
  color: #0277bd;
}

.claude-status.loading {
  background: #fff8e1;
  color: #f57c00;
}

.claude-status:not(.connected):not(.loading) {
  background: #fce4ec;
  color: #c2185b;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.nav-btn {
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.nav-btn:hover {
  background: #e2e8f0;
}

.nav-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ml-training-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: white;
}

.ml-training-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.service-error {
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
}

.error-content {
  background: white;
  border: 2px solid #fecaca;
  border-radius: 0.75rem;
  padding: 3rem;
  text-align: center;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
}

.error-content h3 {
  margin: 0 0 1rem 0;
  color: #dc2626;
  font-size: 1.5rem;
  font-weight: 600;
}

.error-content p {
  margin: 0 0 1rem 0;
  color: #6b7280;
  line-height: 1.6;
}

.error-hint {
  font-size: 0.875rem;
  color: #9ca3af;
  font-style: italic;
}

.retry-service-btn {
  padding: 0.75rem 1.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.2s;
}

.retry-service-btn:hover {
  background: #b91c1c;
}

.dashboard-main {
  max-width: 1400px;
  margin: 0 auto;
  min-height: calc(100vh - 120px);
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 250, 252, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content {
  background: white;
  border-radius: 0.75rem;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-content h3 {
  margin: 0 0 0.5rem 0;
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
}

.loading-content p {
  margin: 0;
  color: #64748b;
}

.breadcrumbs {
  position: fixed;
  bottom: 4rem;
  left: 88%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  background: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.breadcrumb {
  padding: 0.5rem 1rem;
  background: transparent;
  color: #64748b;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.breadcrumb:hover {
  background: #f1f5f9;
  color: #475569;
}

.breadcrumb.active {
  background: #3b82f6;
  color: white;
}

.breadcrumb.active:hover {
  background: #2563eb;
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 1rem 0;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    padding: 0 1rem;
  }
  
  .header-right {
    width: 100%;
    justify-content: space-between;
  }
  
  .breadcrumbs {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    margin: 2rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .service-error {
    margin: 2rem;
    padding: 1rem;
  }
  
  .error-content {
    padding: 2rem;
  }
}
</style>